const SUITS = ["Spades", "Hearts", "Diamonds", "Clubs"];
const RANKS = ["A", "K", "Q", "J", "10", "9", "8", "7"];
const RANK_POWER = {
  A: 8,
  K: 7,
  Q: 6,
  J: 5,
  "10": 4,
  "9": 3,
  "8": 2,
  "7": 1,
};

const SUIT_ICON = {
  Spades: "♠",
  Hearts: "♥",
  Diamonds: "♦",
  Clubs: "♣",
};

export function generateRoomCode() {
  return `OMI-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
}

export function formatSuit(suit) {
  return SUIT_ICON[suit] || suit;
}

export function createDeck() {
  const cards = [];
  SUITS.forEach((suit) => {
    RANKS.forEach((rank, index) => {
      cards.push({
        id: `${suit}-${rank}-${index}`,
        suit,
        rank,
        label: `${formatSuit(suit)} ${rank}`,
      });
    });
  });
  return cards;
}

export function shuffleCards(cards) {
  const next = [...cards];
  for (let index = next.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
  }
  return next;
}

export function getTeamKeyFromSeat(seat) {
  return seat % 2 === 1 ? "teamA" : "teamB";
}

export function getOpposingTeam(teamKey) {
  return teamKey === "teamA" ? "teamB" : "teamA";
}

export function buildOmiMatchState(players) {
  const seatedPlayers = [...players]
    .sort((left, right) => Number(left.seat || 0) - Number(right.seat || 0))
    .map((player, index) => ({
      ...player,
      seat: index + 1,
    }));

  const shuffled = shuffleCards(createDeck());
  const hands = {};

  seatedPlayers.forEach((player, index) => {
    hands[player.userId] = shuffled
      .slice(index * 8, index * 8 + 8)
      .sort((left, right) => RANK_POWER[right.rank] - RANK_POWER[left.rank]);
  });

  return {
    players: seatedPlayers,
    status: "playing",
    turnOrder: seatedPlayers.map((player) => player.userId),
    turnUserId: seatedPlayers[0]?.userId || null,
    hands,
    trick: [],
    lastTrick: [],
    scores: { teamA: 0, teamB: 0 },
    trump: shuffled[shuffled.length - 1]?.suit || SUITS[0],
    round: 1,
    trickNumber: 1,
    winnerTeam: null,
    payoutProcessed: false,
    rematchReady: [],
    finishedReason: null,
    forfeitedBy: null,
  };
}

export function canPlayCardInTrick(card, hand, trick) {
  if (!Array.isArray(trick) || !trick.length) return true;
  const leadSuit = trick[0]?.card?.suit;
  if (!leadSuit) return true;

  const hasLeadSuit = (hand || []).some((entry) => entry.suit === leadSuit);
  if (!hasLeadSuit) return true;
  return card?.suit === leadSuit;
}

function getCardPower(card, leadSuit, trump) {
  if (card.suit === trump) return 100 + (RANK_POWER[card.rank] || 0);
  if (card.suit === leadSuit) return 10 + (RANK_POWER[card.rank] || 0);
  return RANK_POWER[card.rank] || 0;
}

export function resolveTrickWinner(trick, players, trump) {
  if (!trick?.length) return null;

  const leadSuit = trick[0].card.suit;
  const playersById = new Map(players.map((player) => [player.userId, player]));

  let winner = trick[0];
  let highestPower = getCardPower(trick[0].card, leadSuit, trump);

  trick.slice(1).forEach((entry) => {
    const power = getCardPower(entry.card, leadSuit, trump);
    if (power > highestPower) {
      winner = entry;
      highestPower = power;
    }
  });

  const winnerPlayer = playersById.get(winner.userId);
  return {
    userId: winner.userId,
    teamKey: getTeamKeyFromSeat(Number(winnerPlayer?.seat || 1)),
    winningCard: winner.card,
    leadSuit,
  };
}
